import { CacheEntities, getFlaggedIdsWithCache } from "@punchup/utils/caches";

import { ShowWithComedian } from "@punchup/data/legacy/shows";
import { logException } from "@punchup/sentry";

export type ShowWithPasswordProtection = ShowWithComedian & {
  is_password_protected?: boolean;
};

interface PasswordProtectionResult {
  eventId: string;
  isPasswordProtected: boolean;
  hidden: boolean;
  error?: string;
}

interface BulkPasswordProtectionResponse {
  results: PasswordProtectionResult[];
}

export const HIDDEN_LOOKUP_CACHE_TTL_SECONDS = 60 * 60;

/**
 * One bulk-password-protection call. Throws on a failed response so a cached
 * caller never memoises an outage as "nothing hidden".
 */
async function fetchHiddenEventIds(eventIds: string[]): Promise<Set<string>> {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_TIXOLOGI_API_URL}/public/ticketing/bulk-password-protection`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ eventIds }),
    }
  );

  if (!response.ok) {
    throw new Error("[Tixologi] password protected shows request failed");
  }

  const data: BulkPasswordProtectionResponse = await response.json();
  const hidden = new Set<string>();
  data.results.forEach((result) => {
    if (!result.error && (result.isPasswordProtected || result.hidden)) {
      hidden.add(result.eventId);
    }
  });
  return hidden;
}

/**
 * Looks up which of the given Tixologi event ids must not be shown - password
 * protected or hidden upstream. One bulk call.
 *
 * Fails OPEN (empty set): the upstream is advisory, and a lookup failure must
 * not blank out a show listing. Callers therefore cannot distinguish "nothing
 * hidden" from "could not ask".
 *
 * @param eventIds - Tixologi event ids to check
 * @returns The subset of ids that must be hidden
 */
export async function getHiddenTixologiEventIds(
  eventIds: string[]
): Promise<Set<string>> {
  if (eventIds.length === 0 || !process.env.NEXT_PUBLIC_TIXOLOGI_API_URL) {
    return new Set<string>();
  }

  try {
    return await fetchHiddenEventIds(eventIds);
  } catch (error) {
    logException(error, { tags: { tixologi: true } });
    return new Set<string>();
  }
}

function tixologiEventIds(shows: ShowWithComedian[]): string[] {
  return shows
    .map((show) => show.tixologi_event_id)
    .filter((id): id is string => !!id);
}

function withoutHidden(
  shows: ShowWithComedian[],
  hidden: Set<string>
): ShowWithComedian[] {
  return shows.filter(
    (show) => !show.tixologi_event_id || !hidden.has(show.tixologi_event_id)
  );
}

/**
 * Given an array of shows, filters out shows that are password protected or hidden.
 * It does so by looking up in tixologi if any tixologi shows from the original array are password protected or hidden
 * and excludes them from the returned array.
 */
export async function filterOutPasswordProtectedShows(
  shows: ShowWithComedian[]
): Promise<ShowWithComedian[]> {
  const eventIds = tixologiEventIds(shows);

  if (eventIds.length === 0) {
    return shows;
  }

  return withoutHidden(shows, await getHiddenTixologiEventIds(eventIds));
}

/**
 * Batch-job variant of [[filterOutPasswordProtectedShows]] for callers that run
 * the same lookup once per user (the Thursday digest). Each event id reaches
 * bulk-password-protection at most once per HIDDEN_LOOKUP_CACHE_TTL_SECONDS
 * across every caller, through a shared Redis memo. Fails open like the
 * uncached filter.
 */
export async function filterOutPasswordProtectedShowsCached(
  shows: ShowWithComedian[]
): Promise<ShowWithComedian[]> {
  const eventIds = tixologiEventIds(shows);

  if (eventIds.length === 0 || !process.env.NEXT_PUBLIC_TIXOLOGI_API_URL) {
    return shows;
  }

  try {
    const hidden = await getFlaggedIdsWithCache({
      entity: CacheEntities.TIXOLOGI_EVENT_HIDDEN,
      ids: eventIds,
      ttl: HIDDEN_LOOKUP_CACHE_TTL_SECONDS,
      fetchFlagged: fetchHiddenEventIds,
    });
    return withoutHidden(shows, hidden);
  } catch (error) {
    logException(error, { tags: { tixologi: true } });
    return shows;
  }
}
